import React from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"

import { useAuth, useAlert } from "hooks"

import dateService from "services/date"
import { historyTypes, ONE_DAY_IN_SECONDS, CANCELLED_REQUEST, BACKSTOSTORE_REQUEST_ACCEPTED, BUY_REQUEST_ACCEPTED, BACKSTOSTORE_REQUEST_FORCED, BACKSTOSTORE_REQUEST_REFUSED } from "utils/constants"
import { getJsonPipeFlow } from "utils/jsonPipe"
import FormElement from "components/Form/FormElement"
import { isNotEmpty } from "utils"

const sortBikesByDateProperty = (dateProperty) => (bikeA, bikeB) => dateService.getTimestamp(bikeB?.[dateProperty]) - dateService.getTimestamp(bikeA?.[dateProperty])

const defaultFormEvents = {
    ready: true
}

export const ExportData = () => {
    const { t } = useTranslation()
    const { logout } = useAuth()
    const { alert } = useAlert()

    const defaultFormFields = React.useMemo(() => ({
        startDate: new Date(),
        endDate: new Date(),
    }), [])

    const [formFields, setFormFields] = React.useState(defaultFormFields)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)

    const handleChange = (event) => {
        const target = event.target
        const value = target.value
        const name = target.name

        setFormFields({
            ...formFields,
            [name]: value
        })
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    // Headers translations
    const DATE_KEY = t("history.activity_headers.date", { ns: "partner" })
    const BIKE_ID_KEY = t("history.activity_headers.code", { ns: "partner" })
    const BRAND_KEY = t("history.activity_headers.brand", { ns: "partner" })
    const MODEL_KEY = t("history.activity_headers.model", { ns: "partner" })
    const VAE_KEY = t("history.activity_headers.vae", { ns: "partner" })
    const COLOR_KEY = t("history.activity_headers.color", { ns: "partner" })
    const MANUFACTURER_KEY = t("history.activity_headers.manufacturer_name", { ns: "partner" })
    const ACTION_KEY = t("history.export.requestType", { ns: "partner" })
    const RETURN_TYPE_KEY = t("history.export.returnType", { ns: "partner" })
    const EXPIRE_KEY = t("history.activity_headers.isExpired", { ns: "partner" })

    const sortBikesByUnknownDateProperty = (bikes) => {
        if (!bikes || bikes.length === 0) return null
        // if we don't know the date property, we could parse the given
        // existing date properties and check the one that works
        
        const existingDateProperties = Object.values(historyTypes).map(bike => bike.dateProperty)
        for (const dateProperty of existingDateProperties) {
            if (bikes[0].hasOwnProperty(dateProperty)) {
                return sortBikesByDateProperty(dateProperty)
            }
        }
        return null
    }

    const getStreamBikesActiviy = async ({
        bikesActivityProps,
        onComplete
    }) => {
        try {
            return new Promise(async (resolve) => {
                // We need to add or sub one day when we use time range to fetch bikes
                const newStartDate = dateService.subDays(new Date(formFields.startDate), 1) // Substract 1 day for created_after
                const newEndDate = dateService.addDays(new Date(formFields.endDate), 1) // Adding 1 day for created_before

                // Detect if start date and end date are the same
                const isSameDay = dateService.isSameDay(new Date(formFields.startDate), new Date(formFields.endDate))

                const isPreSavedBikes = bikesActivityProps.value === "PRE-SAVED-BIKES"
    
                const formatedNewStartDate = dateService.formatDate(newStartDate, "yyyy-MM-dd")
                const formatedEndStartDate = dateService.formatDate(newEndDate, "yyyy-MM-dd")

                let streamedBikesObject = {}

                const preRegisteredHistoryRoute = 
                    (isPreSavedBikes || isSameDay)
                    ? `${bikesActivityProps.route}?${bikesActivityProps.routeParam}=${dateService.formatDate(formFields.startDate, "yyyy-MM-dd")}`
                    : `${bikesActivityProps.route}?${bikesActivityProps.routeParam}=${formatedNewStartDate}&created_before=${formatedEndStartDate}`
    
                const onPreregisteredHistoryProgress = (data) => {
                    // The data streamed is saved as an object with key/value
                    // to avoid duplicate keys (ie duplicate bike)
                    streamedBikesObject = {
                        ...streamedBikesObject,
                        [data.bike_id]: data
                    }
                }
                const onPreregisteredHistoryError = (error) => {
                    throw bikesActivityProps.getError(error)
                }
                const onPreregisteredHistoryComplete = () => {
                    const streamedBikes = Object.values(streamedBikesObject)
                    const bikesHistory = streamedBikes
                    // Remove delete bikes from Array (status 9). Available on pre-saved-bike
                    let bikeHistoryFiltered = bikesHistory.filter(bike => bike?.status !== 9)
                    // Only keep a given quantity of bikes (--> filtering func), and the most recent ones (--> sorting func)
                    // (a reason to do that is that the bikesArray is then stringified before being saved in storage.
                    // But some browsers such as Safari cannot save in storage a too long string.)
                    if (bikeHistoryFiltered && bikeHistoryFiltered.length > 0) {
                        bikeHistoryFiltered = bikeHistoryFiltered
                                                .sort(sortBikesByUnknownDateProperty(bikesHistory))
                    }

                    resolve(onComplete(bikeHistoryFiltered))
                }
    
                await getJsonPipeFlow({
                    apiRoute: preRegisteredHistoryRoute,
                    onProgress: onPreregisteredHistoryProgress,
                    onComplete: onPreregisteredHistoryComplete,
                    onError: onPreregisteredHistoryError,
                    utils: { logout }
                })
            })
        } catch (error) {
            console.error(error)
            if (["NotAuthorizedException"].includes(error?.code)) logout()
        }
    }

    let bikesHistoryOnGoing = []

    const prepareOnGoing = (bikeData) => {
        return bikesHistoryOnGoing = bikeData
            .filter(bike => [...historyTypes.onGoing.operationNameList].includes(bike?.operation))
            .map(bike => {
                const isExpired = dateService.getTimestamp(bike.create_date) + ONE_DAY_IN_SECONDS < new Date().getTime()
                let operation = "partner:history.on-going-filters.waiting"
                if (bike.operation === CANCELLED_REQUEST) operation = "partner:history.operation.cancelled"
                if (bike.operation === BACKSTOSTORE_REQUEST_REFUSED) operation = "partner:history.operation.backtostore-refused"
                return {
                    [DATE_KEY]: dateService.formatDate(new Date(bike.create_date), dateService.getCurrentDateFormat()),
                    [BIKE_ID_KEY]: bike.bike_id,
                    [BRAND_KEY]: bike.bike_description?.brand || "",
                    [MODEL_KEY]: bike.bike_description?.model || "",
                    [VAE_KEY]: bike.bike_description?.is_electric ? t("global.yes") : t("global.no"),
                    [COLOR_KEY]: bike.bike_description?.color || "",
                    [MANUFACTURER_KEY]: bike.manufacturer_name || "",
                    [ACTION_KEY]: t(operation) || "",
                    [EXPIRE_KEY]: isExpired ? t("global.yes") : t("global.no")
                }
            })
    }

    let bikesHistoryPreSavedBikes = []

    const preparePreRegistered = (bikeData) => {
        return bikesHistoryPreSavedBikes = bikeData.map(bike => {
            return {
                [DATE_KEY]: dateService.formatDate(new Date(bike.create_date), dateService.getCurrentDateFormat()),
                [BIKE_ID_KEY]: bike.bike_id,
                [BRAND_KEY]: bike.bike_description?.brand || "",
                [MODEL_KEY]: bike.bike_description?.model || "",
                [VAE_KEY]: bike.bike_description?.is_electric ? t("global.yes") : t("global.no"),
                [COLOR_KEY]: bike.bike_description?.color || "",
                [MANUFACTURER_KEY]: bike.manufacturer_name || "",
            }
        })
    }


    let bikesHistorySavedBikes = []

    const prepareRegistered = (bikeData) => {
        return bikesHistorySavedBikes = bikeData
            .filter(bike => [...historyTypes.activated.operationNameList].includes(bike?.operation))
            .map(bike => {
                return {
                    [DATE_KEY]: dateService.formatDate(new Date(bike.create_date), dateService.getCurrentDateFormat()),
                    [BIKE_ID_KEY]: bike.bike_id,
                    [BRAND_KEY]: bike.bike_description?.brand || "",
                    [MODEL_KEY]: bike.bike_description?.model || "",
                    [VAE_KEY]: bike.bike_description?.is_electric ? t("global.yes") : t("global.no"),
                    [COLOR_KEY]: bike.bike_description?.color || "",
                    [MANUFACTURER_KEY]: bike.manufacturer_name || "",
                }
            })
    }

    let bikesHistoryBuyBack = []

    const prepareBackAndBuy = (bikeData) => {
        return bikesHistoryBuyBack = bikeData
            .filter(bike => [...historyTypes.backAndBuy.operationNameList].includes(bike?.operation))
            .map(bike => {
                let operation
                if (bike.operation === BACKSTOSTORE_REQUEST_ACCEPTED) operation = "partner:history.confirmed-filters.backtostore-confirmed"
                if (bike.operation === BUY_REQUEST_ACCEPTED) operation = "partner:history.confirmed-filters.buy-confirmed"
                if (bike.operation === BACKSTOSTORE_REQUEST_FORCED) operation = "partner:history.confirmed-filters.backtostore-forced"

                return {
                    [DATE_KEY]: bike.create_date,
                    [BIKE_ID_KEY]: bike.bike_id,
                    [BRAND_KEY]: bike.bike_description?.brand || "",
                    [MODEL_KEY]: bike.bike_description?.model || "",
                    [VAE_KEY]: bike.bike_description?.is_electric ? t("global.yes") : t("global.no"),
                    [COLOR_KEY]: bike.bike_description?.color || "",
                    [MANUFACTURER_KEY]: bike.manufacturer_name || "",
                    [RETURN_TYPE_KEY]: operation ? t(operation) : bike?.operation || ""
                }
            })
    }

    const exportHistory = async (event) => {
        event.preventDefault()
        // Validate inputs
        const isValidTimeRange = new Date(formFields.startDate) <= new Date(formFields.endDate)
        if(!isValidTimeRange) alert("error", t("history.export.isValidTimeRange", { ns: "partner" }))

        const formReady = !(errors.length > 0) && isValidTimeRange

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            // prevent form to be submitted again
            setFormEvents(prevFormEvents => ({
                ...prevFormEvents,
                ready: false
            }))

            try {
                // Get all bikes data
                await Promise.all([
                    getStreamBikesActiviy({
                        bikesActivityProps: historyTypes.onGoing,
                        onComplete: (response) => {
                            // Here we just need to call once API
                            prepareOnGoing(response)
                            prepareRegistered(response)
                            prepareBackAndBuy(response)
                        }
                    }),
                    getStreamBikesActiviy({
                        bikesActivityProps: historyTypes.preregistered,
                        onComplete: (response) => preparePreRegistered(response)
                    }),
                ]).then(() => {
                    // build Excel
                    const Header = [DATE_KEY, BIKE_ID_KEY, BRAND_KEY, MODEL_KEY, VAE_KEY, COLOR_KEY, MANUFACTURER_KEY]
                    const wb = XLSX.utils.book_new()

                    /* Add on going page */
                    if(isNotEmpty(bikesHistoryOnGoing)) {
                        const wsOnGoingBikes = XLSX.utils.json_to_sheet(bikesHistoryOnGoing)
                        XLSX.utils.sheet_add_json(wsOnGoingBikes, [], {
                            origin: "A1",
                            header: [...Header, ACTION_KEY, EXPIRE_KEY]
                        })
                        XLSX.utils.book_append_sheet(wb, wsOnGoingBikes, t("history.activity_types.ongoing", { ns: "partner" }))
                    }

                    /* Add pre-registered page */
                    if(isNotEmpty(bikesHistoryPreSavedBikes)) {
                        const wsPreSavedBikes = XLSX.utils.json_to_sheet(bikesHistoryPreSavedBikes)
                        XLSX.utils.sheet_add_json(wsPreSavedBikes, [], {
                            origin: "A1",
                            header: Header
                        })
                        XLSX.utils.book_append_sheet(wb, wsPreSavedBikes, t("history.activity_types.pre_saved", { ns: "partner" }))
                    }

                    /* Add registered page */
                    if(isNotEmpty(bikesHistorySavedBikes)) {
                        const wsSavedBikes = XLSX.utils.json_to_sheet(bikesHistorySavedBikes)
                        XLSX.utils.sheet_add_json(wsSavedBikes, [], {
                            origin: "A1",
                            header: Header
                        })
                        XLSX.utils.book_append_sheet(wb, wsSavedBikes, t("history.activity_types.saved", { ns: "partner" }))
                    }

                    /* Add back en buy page */
                    if(isNotEmpty(bikesHistoryBuyBack)) {
                        const wsBuyBackBikes = XLSX.utils.json_to_sheet(bikesHistoryBuyBack)
                        XLSX.utils.sheet_add_json(wsBuyBackBikes, [], {
                            origin: "A1",
                            header: [...Header, RETURN_TYPE_KEY]
                        })
                        XLSX.utils.book_append_sheet(wb, wsBuyBackBikes, t("history.activity_types.back_and_buy_to_store", { ns: "partner" }).replace("/", "-").slice(0, 30))
                    }

                    // download file
                    const fname = t("history.export.fileName", { ns: "partner" })
                    if(isNotEmpty(wb?.SheetNames)) XLSX.writeFile(wb, fname + ".xlsx")

                    if(!isNotEmpty(wb?.SheetNames)) alert("error", t("history.export.noDataToExport", { ns: "partner" }))
                })
            } catch (error) {
                alert("error", t("history.export.errorDownloadFile", { ns: "partner" }))
            } finally {
                setFormEvents(prevFormEvents => ({
                    ...prevFormEvents,
                    ready: true
                }))
            }
        }
    }

    const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

    return (
        <form className="">
            <section className="mb-5">
                <h2 className="c-h2 text-primary font-bold uppercase mb-5">
                    {t("history.export.title", { ns: "partner" })}
                </h2>
                <p className="mb-7">{t("history.export.description", { ns: "partner" })}</p>
                <div className="flex flex-row-dir u-mg-negative-hz-m">
                    <div className="l-col-12 p-5">
                        <FormElement
                            {...formElementCommonProps}
                            value={formFields.startDate}
                            name="startDate"
                            type="date"
                            label={t("history.export.startingDate", { ns: "partner" })}
                            className="mb-5"
                            required={true}
                            min={dateService.sub(new Date(), { years: 1 })} // max 1 year back
                            max={dateService.getCurrentDate().toString()} // max today
                        />
                    </div>
                    <div className="l-col-12 p-5">
                        <FormElement
                            {...formElementCommonProps}
                            value={formFields.endDate}
                            name="endDate"
                            type="date"
                            label={t("history.export.endingDate", { ns: "partner" })}
                            className="mb-5"
                            required={true}
                            min={dateService.sub(new Date(), { years: 1 })} // max 1 year back
                            max={new Date()} // max today
                        />
                    </div>
                </div>
            </section>
            <div className="flex justify-end">
                {formEvents.ready &&
                    <button
                        type="button"
                        className="c-btn c-btn--primary px-12 mb-5 lg:m-0"
                        disabled={errors.length > 0}
                        onClick={exportHistory}
                    >
                        {t("global.validate")}
                    </button>
                }
                {!formEvents.ready &&
                    <div className="c-spinner"></div>
                }
            </div>
        </form>
    )
}
